import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { 
  Building2,
  Plus, 
  Search, 
  BookOpen, 
  Calendar, 
  MoreVertical, 
  FileText, 
  Edit2, 
  Trash2, 
  Clock,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Layout,
  Upload,
  Folder,
  File,
  Heart,
  Monitor,
  Calculator, 
  FlaskConical, 
  Globe, 
  Code2, 
  Library,
  LayoutList,
  ArrowRight,
  PenLine,
  Target,
  Database,
  Loader2,
  Download,
  Layers,
  AlertCircle,
  Eye,
  Wind,
  Building,
  CheckCircle2,
  Layers2,
  Settings,
  HelpCircle,
  GraduationCap,
} from 'lucide-react';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { useNavigate, useLocation } from 'react-router-dom';
import { db, auth, storage } from '../../firebase/config';
import { collection, onSnapshot, query, orderBy, where, addDoc, serverTimestamp, deleteDoc, doc, getDocs, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { Toaster, toast } from "sonner";
import { subjectCategories } from './subjectCategories';
import WorkspaceLoader from './WorkspaceLoader';
import { PersonalResourceCard } from './CourseMaterialsView';
import { PDFReader } from './IndependentResourceComponents';
import { cn } from '@/lib/utils';

interface Lesson {
  id: string;
  title: string;
  subject: string;
  subjectId: string;
  date: string;
  status: 'draft' | 'published';
  lastModified: string;
  selectedTopic?: string;
  lessonScope?: 'subject' | 'topic';
}

const hasMeaningfulOutlineContent = (html?: string | null) => {
  if (typeof html !== 'string') return false;

  const normalizedHtml = html.trim();
  if (!normalizedHtml) return false;

  const hasNonTextContent = /<(img|iframe|video|audio|table|ul|ol|li|blockquote|pre|code|embed|object|svg|hr)\b/i.test(normalizedHtml);
  const textOnly = normalizedHtml
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;|&#160;/gi, ' ')
    .replace(/&[a-z0-9#]+;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return hasNonTextContent || textOnly.length > 0;
};

const getStoredContentStatus = (data?: Record<string, any> | null) => {
  const hasPdf = typeof data?.referencePdfUrl === 'string' && data.referencePdfUrl.trim().length > 0;
  const hasOutline = hasMeaningfulOutlineContent(data?.outlineContent);

  return {
    hasPdf,
    hasOutline,
    hasAnyContent: hasPdf || hasOutline,
  };
};

const buildProgressScopeId = (school?: string | null, className?: string | null) => {
  const normalize = (value?: string | null, fallback = 'unassigned') => {
    const normalized = (value || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');

    return normalized || fallback;
  };

  return `resume__${normalize(school, 'all-schools')}__${normalize(className, 'all-classes')}`;
};

const LessonPlannerView = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Initialise from location.state immediately so returning from the content viewer
  // never causes a flash of the subject-list before the useEffect fires.
  const [selectedSubject, setSelectedSubject] = useState<string | null>(
    () => (location.state as any)?.subjectName || null
  );
  const [activeTab, setActiveTab] = useState<'plans' | 'resources'>('resources');
  const [searchTerm, setSearchTerm] = useState('');
  const [lessons, setLessons] = useState<Lesson[]>([]);
  // selectedSchool = school name; selectedClass = class name within that school
  const [selectedSchool, setSelectedSchool] = useState<string>(
    () => (location.state as any)?.selectedSchool || ''
  );
  const [selectedClass, setSelectedClass] = useState<string>(
    () => (location.state as any)?.selectedClass || ''
  );
  const [addPrompt, setAddPrompt] = useState<{
    open: boolean;
    kind: 'school' | 'class';
    value: string;
    saving: boolean;
  }>({ open: false, kind: 'school', value: '', saving: false });
  const [syllabusData, setSyllabusData] = useState<any>(null);
  const [subContentMap, setSubContentMap] = useState<Record<string, any>>({});
  const [subContentLoaded, setSubContentLoaded] = useState(false);
  const [resumeProgress, setResumeProgress] = useState({
    progressPercent: 0,
    currentPage: 0,
    totalPages: 0,
    isLoaded: false,
    isCompleted: false,
    completedAt: null as string | null,
  });
  const [subjectProgressMap, setSubjectProgressMap] = useState<Record<string, { progressPercent: number; isCompleted: boolean }>>({}); 
  const [nextLessonMap, setNextLessonMap] = useState<Record<string, string>>({});
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [expandedTopicId, setExpandedTopicId] = useState<string | null>(null);

  const handleSelectSubject = (subjectName: string | null) => {
    setSelectedSubject(subjectName);
    const currentState = (location.state as any) || {};
    const newState = { ...currentState };
    if (subjectName) {
      newState.subjectName = subjectName;
    } else {
      delete newState.subjectName;
    }
    navigate(location.pathname, { replace: true, state: newState });
  };
  
  // Lesson Delete State
  const [lessonToDelete, setLessonToDelete] = useState<string | null>(null);
  const [isDeletingLesson, setIsDeletingLesson] = useState(false);

  const filteredLessons = lessons.filter(l => 
    (l.subject === selectedSubject) && 
    (l.title.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  useEffect(() => {
    // Only update when navigating back from the viewer with fresh state
    // (do not wipe values if location.state doesn't carry them)
    if (location.state?.subjectName) {
      setSelectedSubject(location.state.subjectName);
    }
    if (location.state?.selectedSchool) {
      setSelectedSchool(location.state.selectedSchool);
    }
    if (location.state?.selectedClass) {
      setSelectedClass(location.state.selectedClass);
    }
  }, [location.state]);

  const [dbSubjects, setDbSubjects] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [subjectAssetCounts, setSubjectAssetCounts] = useState<{[subjectId: string]: number}>({});
  const [subjectResources, setSubjectResources] = useState<any[]>([]);
  const [favourites, setFavourites] = useState<Set<string>>(new Set());
  const [downloadingResourceId, setDownloadingResourceId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [viewResource, setViewResource] = useState<any | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  // Track in-flight navigations to the structure page so the "Manage Resource"
  // button can show a spinner + the page gets a brief full-screen overlay
  // before the destination's own loader takes over.
  const [isOpeningStructure, setIsOpeningStructure] = useState(false);
  const openStructurePage = () => {
    if (!activeSubjectId || !selectedSubject) {
      toast.error('Select a subject first');
      return;
    }
    setIsOpeningStructure(true);
    // brief delay so the spinner is visible even on instant transitions
    window.setTimeout(() => {
      navigate('/dashboard/lessons/course/structure', {
        state: {
          subjectId: activeSubjectId,
          subjectName: selectedSubject,
          isFirebaseSubject: !!activeSubjectObj
        }
      });
    }, 50);
  };
  const activeSubjectObj = dbSubjects.find(s => s.name === selectedSubject);
  const activeSubjectId = activeSubjectObj ? activeSubjectObj.id : selectedSubject?.toLowerCase().replace(/\s+/g, '-') || 'unknown';

// helper to calculate assets (resources + syllabus PDFs) for a subject
  const countAssetsForSubject = async (uid: string, subjectId: string) => {
    // count resources matching pdf/word types
    const resourcesCol = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/resources`);
    const resourcesSnap = await getDocs(resourcesCol);
    let resourceCount = resourcesSnap.docs.filter(doc => {
      const data = doc.data();
      return (
        data.type && (
          data.type.includes('pdf') ||
          data.type.includes('word') ||
          data.type.includes('msword') ||
          data.type.includes('doc') ||
          data.type.includes('docx') ||
          data.type.includes('ppt') ||
          data.type.includes('presentation')
        )
      );
    }).length;

    // count any saved syllabus/pdf references and typed outlines
    const syllabusCol = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`);
    const syllabusSnap = await getDocs(syllabusCol);
    const extraCount = syllabusSnap.docs.reduce((acc, d) => {
      const data = d.data();
      const contentStatus = getStoredContentStatus(data);
      return acc + Number(contentStatus.hasPdf) + Number(contentStatus.hasOutline);
    }, 0);

    return resourceCount + extraCount;
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const q = query(collection(db, `teaching_workspaces/${uid}/courses`), orderBy('createdAt', 'desc'));

    const unsubscribe = onSnapshot(q, async (snapshot) => {
      const fetched = snapshot.docs.map(doc => {
        const data = doc.data();
        const cat = subjectCategories.find((c: any) => c.value === data.category);
        const legacyClassCourses = Array.isArray(data.classCourses) ? data.classCourses : [];
        // Normalise schoolsData from either rich array or legacy flat string array
        let schoolsData: { name: string; classes: string[] }[] = [];
        if (Array.isArray(data.schoolsData) && data.schoolsData.length > 0) {
          schoolsData = data.schoolsData.map((school: any) => ({
            name: school.name || '',
            classes: Array.isArray(school.classes) && school.classes.length > 0 ? school.classes : legacyClassCourses,
          }));
        } else if (Array.isArray(data.schools) && data.schools.length > 0) {
          schoolsData = data.schools.map((s: string) => ({ name: s, classes: legacyClassCourses }));
        }
        return {
          id: doc.id,
          name: data.title,
          icon: cat ? cat.icon : BookOpen,
          theme: cat ? `${cat.color} ${cat.bg} ${cat.border}` : 'text-slate-600 bg-slate-100 dark:bg-slate-800 border-slate-200',
          barColor: cat ? cat.color.replace('text-', 'bg-') : 'bg-slate-600',
          stats: { plans: 0, assets: 0, hours: data.approxHours || 0, progress: 0 },
          nextLesson: 'Setup required',
          isMultiSchool: data.isMultiSchool,
          schools: (data.schools || []) as string[],
          schoolsData,
          classCourses: legacyClassCourses,
          defaultSchool: data.defaultSchool || '',
          defaultClass: data.defaultClass || ''
        };
      });
      setDbSubjects(fetched);

      // Fetch asset counts for each subject
      const assetCounts: {[subjectId: string]: number} = {};
      await Promise.all(fetched.map(async (subject) => {
        const count = await countAssetsForSubject(uid, subject.id);
        assetCounts[subject.id] = count;
      }));
      setSubjectAssetCounts(assetCounts);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Load max reading progress for every subject (shown on subject cards)
  useEffect(() => {
    if (!auth.currentUser || !dbSubjects.length) return;
    let cancelled = false;
    const uid = auth.currentUser.uid;
    const load = async () => {
      const entries: Record<string, { progressPercent: number; isCompleted: boolean }> = {};
      await Promise.all(
        dbSubjects.map(async (subject) => {
          try {
            const snap = await getDocs(
              collection(db, `teaching_workspaces/${uid}/courses/${subject.id}/reading_progress`)
            );
            let maxPercent = 0;
            let anyCompleted = false;
            snap.docs.forEach(d => {
              const d2 = d.data();
              maxPercent = Math.max(maxPercent, Number(d2.progressPercent || 0));
              if (d2.isCompleted) anyCompleted = true;
            });
            entries[subject.id] = { progressPercent: maxPercent, isCompleted: anyCompleted };
          } catch {}
        })
      );
      if (!cancelled) setSubjectProgressMap(entries);
    };
    load();
    return () => { cancelled = true; };
  }, [dbSubjects]);

  // Load all lessons across all subjects
  useEffect(() => {
    if (!auth.currentUser) return;
    const uid = auth.currentUser.uid;
    const lessonsRef = collection(db, `teaching_workspaces/${uid}/lessons`);
    const unsubscribe = onSnapshot(lessonsRef, (snapshot) => {
      const fetchedLessons = snapshot.docs.map(docSnap => {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          title: data.title || 'Untitled Lesson',
          subject: data.subjectName || '',
          subjectId: data.subjectId || '',
          date: data.updatedAt?.toDate ? data.updatedAt.toDate().toLocaleDateString() : 'Just now',
          status: 'published' as const,
          lastModified: data.updatedAt?.toDate ? data.updatedAt.toDate().toISOString() : new Date().toISOString(),
          selectedTopic: data.selectedTopic || '',
          lessonScope: data.lessonScope || 'subject',
        };
      });
      // Sort by descending lastModified date
      fetchedLessons.sort((a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime());
      setLessons(fetchedLessons);
    });

    return () => unsubscribe();
  }, []);

  // Compute NEXT topic for each subject based on syllabus + lesson plans
  useEffect(() => {
    if (!auth.currentUser || !dbSubjects.length) return;
    let cancelled = false;
    const uid = auth.currentUser.uid;

    const computeNextLessons = async () => {
      const map: Record<string, string> = {};

      await Promise.all(dbSubjects.map(async (subject) => {
        try {
          const mainDoc = await getDoc(doc(db, `teaching_workspaces/${uid}/courses/${subject.id}/syllabus_data/main`));
          if (!mainDoc.exists()) {
            map[subject.id] = 'No syllabus — add topics first';
            return;
          }
          const syllabus = mainDoc.data();
          const topics: any[] = syllabus.topics || [];
          if (topics.length === 0) {
            map[subject.id] = 'No topics — add topics to your syllabus';
            return;
          }

          const subjectLessons = lessons.filter(l => l.subjectId === subject.id);
          const subjectLevelLessons = subjectLessons.filter(l => l.lessonScope === 'subject');
          if (subjectLevelLessons.length > 0) {
            map[subject.id] = 'Full subject lesson plan created';
            return;
          }

          const coveredTopics = new Set<string>();
          const coveredSubTopics = new Set<string>();
          subjectLessons.forEach(l => {
            if (l.selectedTopic) {
              const parts = l.selectedTopic.split('__');
              if (parts.length === 2) {
                coveredSubTopics.add(`${parts[0]}__${parts[1]}`);
              } else {
                coveredTopics.add(parts[0]);
              }
            }
          });

          for (const topic of topics) {
            if (coveredTopics.has(topic.id)) continue;

            if (topic.subTopics && topic.subTopics.length > 0) {
              for (const sub of topic.subTopics) {
                if (!coveredSubTopics.has(`${topic.id}__${sub.id}`)) {
                  map[subject.id] = sub.title;
                  return;
                }
              }
            } else {
              map[subject.id] = topic.title;
              return;
            }
          }

          map[subject.id] = 'All topics planned';
        } catch (err) {
          console.error(`Failed to compute next lesson for ${subject.id}:`, err);
          map[subject.id] = 'Setup required';
        }
      }));

      if (!cancelled) setNextLessonMap(map);
    };

    computeNextLessons();
    return () => { cancelled = true; };
  }, [dbSubjects, lessons]);

  const persistCourseSettings = async (subjectId: string, payload: Record<string, any>) => {
    if (!auth.currentUser || !subjectId || Object.keys(payload).length === 0) return;
    const uid = auth.currentUser.uid;
    const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
    await updateDoc(courseRef, payload);
  };

  const handleDeleteSubject = async (subjectId: string) => {
    if (!subjectId || !auth.currentUser) return;
    setIsDeleting(true);
    try {
      const uid = auth.currentUser.uid;
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, subjectId);
      
      // Delete syllabusdocs
      const syllabusCol = collection(db, `teaching_workspaces/${uid}/courses/${subjectId}/syllabus_data`);
      const syllabusSnap = await getDocs(syllabusCol);
      const deletePromises = syllabusSnap.docs.map((d: any) => deleteDoc(d.ref));
      await Promise.all(deletePromises);

      await deleteDoc(courseRef);
      toast.success("Subject deleted successfully");
      handleSelectSubject(null);
    } catch (err) {
      console.error("Error deleting subject:", err);
      toast.error("Failed to delete subject");
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
    }
  };

  const handleDeleteLesson = async () => {
    if (!lessonToDelete || !auth.currentUser) return;
    setIsDeletingLesson(true);
    try {
      const uid = auth.currentUser.uid;
      await deleteDoc(doc(db, `teaching_workspaces/${uid}/lessons`, lessonToDelete));
      toast.success("Lesson plan deleted successfully");
    } catch (err) {
      console.error("Error deleting lesson:", err);
      toast.error("Failed to delete lesson plan");
    } finally {
      setIsDeletingLesson(false);
      setLessonToDelete(null);
    }
  };

  useEffect(() => {
    if (!auth.currentUser) return;
    if (!selectedSubject) {
      setSyllabusData(null);
      setSubContentMap({});
      setSubContentLoaded(false);
      setSubjectResources([]);
      return;
    }
    const uid = auth.currentUser.uid;
    const activeSubjectObj = dbSubjects.find(s => s.name === selectedSubject);
    const activeSubjectId = activeSubjectObj ? activeSubjectObj.id : selectedSubject.toLowerCase().replace(/\s+/g, '-');

    setSyllabusData(null);
    setSubContentMap({});
    setSubContentLoaded(false);
    
    // Read from the personal library (teaching_workspaces/{uid}/personal_resources) and
    // filter by the currently selected subject so the inline preview matches the
    // materials the user uploaded via the "My Materials" page.
    // We deliberately avoid `orderBy` here — combining `where` + `orderBy` on
    // different fields requires a composite index, and without one the snapshot
    // silently never delivers. Client-side sort keeps this resilient.
    const q = query(
      collection(db, `teaching_workspaces/${uid}/personal_resources`),
      where('subject', '==', selectedSubject)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetched = snapshot.docs.map(docSnapshot => {
        const data = docSnapshot.data() as any;
        // Map personal-library field names to the shape the inline list + delete
        // handler expect (url, name, type, size, createdAt).
        return {
          id: docSnapshot.id,
          url: data.pdfUrl || data.url || '',
          name: data.title || data.name || 'Untitled',
          type: data.fileType || data.mime || data.type || '',
          size: data.fileSize || data.size || 0,
          createdAt: data.uploadDate || data.createdAt || null,
          subject: data.subject || '',
          ...data,
        };
      });
      // Sort by uploadDate desc client-side
      fetched.sort((a: any, b: any) => {
        const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
        const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
        return bTime - aTime;
      });
      setSubjectResources(fetched);
    });

    let unsubscribeSyllabus = () => {};
    if (activeSubjectObj) {
      const syllabusCol = collection(db, `teaching_workspaces/${uid}/courses/${activeSubjectId}/syllabus_data`);
      unsubscribeSyllabus = onSnapshot(syllabusCol, (snapshot) => {
        const contentMap: Record<string, any> = {};
        let nextSyllabusData: any = null;

        snapshot.docs.forEach(docSnap => {
          const id = docSnap.id;
          const data = docSnap.data();

          if (id === 'main') {
            nextSyllabusData = {
              ...data,
              ...getStoredContentStatus(data),
            };
          } else if (id.startsWith('content_')) {
            const mapKey = id.replace('content_', '');
            contentMap[mapKey] = {
              ...data,
              ...getStoredContentStatus(data),
            };
          }
        });

        setSyllabusData(nextSyllabusData);
        setSubContentMap(contentMap);
        setSubContentLoaded(true);
      });
    } else {
      setSyllabusData(null);
      setSubContentMap({});
      setSubContentLoaded(false);
    }

    return () => {
      unsubscribe();
      unsubscribeSyllabus();
    };
  }, [selectedSubject, dbSubjects]);

  // Capture userId from auth (same pattern as IndependentResourcesView bookmarks).
  useEffect(() => {
    const user = auth.currentUser;
    if (user) setUserId(user.uid);
  }, []);

  // Load favourites with getDocs (same simple pattern as handleBookmark in IndependentResourcesView).
  useEffect(() => {
    if (!userId) return;
    const fetchFavourites = async () => {
      const favRef = collection(db, 'teaching_workspaces', userId, 'favourites');
      const s = await getDocs(favRef);
      setFavourites(new Set(s.docs.map(d => d.id)));
    };
    fetchFavourites();
  }, [userId]);

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !auth.currentUser || !selectedSubject) return;

    setIsUploading(true);
    try {
      const uid = auth.currentUser.uid;
      const activeSubjectObj = dbSubjects.find(s => s.name === selectedSubject);
      const activeSubjectId = activeSubjectObj ? activeSubjectObj.id : selectedSubject.toLowerCase().replace(/\s+/g, '-');
      
      const fileRef = ref(storage, `teaching_workspaces/${uid}/courses/${activeSubjectId}/resources/${Date.now()}_${file.name.replace(/\s+/g, '_')}`);
      const uploadTask = await uploadBytesResumable(fileRef, file);
      const downloadURL = await getDownloadURL(uploadTask.ref);
      
      await addDoc(collection(db, `teaching_workspaces/${uid}/courses/${activeSubjectId}/resources`), {
        name: file.name,
        url: downloadURL,
        size: file.size,
        type: file.type,
        createdAt: serverTimestamp()
      });
      
      // bump course doc so listener recalculates asset counts
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, activeSubjectId);
      await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

      // update local count immediately
      const newCount = await countAssetsForSubject(uid, activeSubjectId);
      setSubjectAssetCounts(prev => ({ ...prev, [activeSubjectId]: newCount }));

      toast.success("Resource uploaded successfully");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Failed to upload resource");
    } finally {
      setIsUploading(false);
      e.target.value = '';
    }
  };

  const handleDeleteResource = async (resourceId: string) => {
    if (!auth.currentUser || !selectedSubject) return;
    try {
      const uid = auth.currentUser.uid;
      const activeSubjectObj = dbSubjects.find(s => s.name === selectedSubject);
      const activeSubjectId = activeSubjectObj ? activeSubjectObj.id : selectedSubject.toLowerCase().replace(/\s+/g, '-');

      // Look up the resource first so we can also remove the underlying file in Storage.
      const resRef = doc(db, `teaching_workspaces/${uid}/personal_resources`, resourceId);
      const resSnap = await getDoc(resRef);
      if (resSnap.exists()) {
        const data = resSnap.data() as any;
        const storagePath = data.storagePath;
        if (storagePath) {
          try {
            const storageRef = ref(storage, storagePath);
            await deleteObject(storageRef);
          } catch (e) {
            // Don't block the Firestore delete on a storage error
            console.warn('Storage delete skipped (file may be gone):', e);
          }
        }
      }

      await deleteDoc(resRef);
      // bump course doc timestamp so other views (e.g. the materials page) refresh
      const courseRef = doc(db, `teaching_workspaces/${uid}/courses`, activeSubjectId);
      await updateDoc(courseRef, { resourcesUpdatedAt: serverTimestamp() });

      const newCount = await countAssetsForSubject(uid, activeSubjectId);
      setSubjectAssetCounts(prev => ({ ...prev, [activeSubjectId]: newCount }));

      toast.success("Resource deleted successfully");
    } catch (error) {
      console.error("Error deleting:", error);
      toast.error("Failed to delete resource");
    }
  };

  const handleDownloadResource = async (url: string, title: string, resourceId?: string) => {
    if (resourceId) setDownloadingResourceId(resourceId);
    try {
      toast.info(`Downloading ${title}...`);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      const cleanUrl = url.split('?')[0];
      const fileExtension = cleanUrl.split('.').pop()?.toLowerCase() || 'pdf';
      link.download = `${title}.${fileExtension}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Download Failed', { description: 'Unable to download the resource. Please try again.' });
    } finally {
      if (resourceId) setDownloadingResourceId(null);
    }
  };

  // Open the resource PDF in a new tab (the inline preview lives in the lesson
  // planner page which doesn't host the PDF reader component, so a new tab is
  // the simplest way to give the user a view action that matches the card UI).
  const handleViewResource = (resource: any) => {
    setViewResource(resource);
  };

  // Toggle favourite for a personal resource — same simple pattern as handleBookmark in IndependentResourcesView.
  const handleFavouriteResource = async (resource: any) => {
    if (!userId) return;
    const isFav = favourites.has(resource.id);
    const ref = doc(db, 'teaching_workspaces', userId, 'favourites', resource.id);
    try {
      if (isFav) {
        await deleteDoc(ref);
        setFavourites((prev) => {
          const next = new Set(prev);
          next.delete(resource.id);
          return next;
        });
      } else {
        await setDoc(ref, { resourceId: resource.id, title: resource.title || resource.name || 'Untitled', createdAt: new Date().toISOString() });
        setFavourites((prev) => {
          const next = new Set(prev);
          next.add(resource.id);
          return next;
        });
      }
      // Reload favourites from Firestore to stay in sync (same as working handleBookmark).
      const s = await getDocs(collection(db, 'teaching_workspaces', userId, 'favourites'));
      setFavourites(new Set(s.docs.map(d => d.id)));
    } catch (err) {
      console.error('Favourite toggle error:', err);
      toast.error('Could not update favourite');
    }
  };

  useEffect(() => {
    if (!auth.currentUser || !activeSubjectObj) {
      setSelectedSchool('');
      setSelectedClass('');
      return;
    }

    const schools: string[] = activeSubjectObj.schools || [];

    // Auto-select school: single school? pick it. Saved default? restore it.
    let restoredSchool = '';
    if (schools.length === 1) {
      restoredSchool = schools[0];
      setSelectedSchool(prev => {
        if (prev !== schools[0]) {
          persistCourseSettings(activeSubjectId, { defaultSchool: schools[0], isMultiSchool: false });
        }
        return schools[0];
      });
    } else if (schools.length > 1 && schools.includes(activeSubjectObj.defaultSchool)) {
      restoredSchool = activeSubjectObj.defaultSchool;
      setSelectedSchool(activeSubjectObj.defaultSchool);
    } else {
      setSelectedSchool('');
    }

    // Restore saved default class for the resolved school
    if (restoredSchool && activeSubjectObj.defaultClass) {
      const schoolData = (activeSubjectObj.schoolsData || []).find(
        (s: { name: string; classes: string[] }) => s.name === restoredSchool
      );
      const validClasses: string[] = schoolData?.classes?.filter((c: string) => c.trim()) ||
        (activeSubjectObj.classCourses || []).filter((c: string) => c.trim());
      if (validClasses.includes(activeSubjectObj.defaultClass)) {
        setSelectedClass(activeSubjectObj.defaultClass);
      } else {
        setSelectedClass('');
      }
    } else {
      setSelectedClass('');
    }
  }, [activeSubjectObj?.id]);

  const handleSelectSchool = async (school: string) => {
    setSelectedSchool(school);
    setSelectedClass('');
    if (!activeSubjectObj) return;
    try {
      await persistCourseSettings(activeSubjectId, {
        defaultSchool: school,
        isMultiSchool: (activeSubjectObj.schools || []).length > 1,
      });
    } catch (error) {
      console.error('Error saving default school:', error);
    }
  };

  const handleSelectClass = async (cls: string) => {
    setSelectedClass(cls);
    if (!activeSubjectObj) return;
    try {
      await persistCourseSettings(activeSubjectId, { defaultClass: cls });
    } catch (error) {
      console.error('Error saving default class:', error);
    }
  };

  const openAddSchoolPrompt = () => {
    if (!activeSubjectObj) return;
    setAddPrompt({ open: true, kind: 'school', value: '', saving: false });
  };

  const openAddClassPrompt = () => {
    if (!activeSubjectObj || !selectedSchool) {
      toast.error('Pick a school first');
      return;
    }
    setAddPrompt({ open: true, kind: 'class', value: '', saving: false });
  };

  const closeAddPrompt = () => {
    if (addPrompt.saving) return;
    setAddPrompt((p) => ({ ...p, open: false, value: '' }));
  };

  const handleConfirmPrompt = async () => {
    if (!activeSubjectObj) return;
    const clean = addPrompt.value.trim();
    if (!clean) return;
    if (addPrompt.kind === 'school') {
      const existing = (activeSubjectObj.schools || []).map((s: string) => s.toLowerCase());
      if (existing.includes(clean.toLowerCase())) {
        setSelectedSchool(clean);
        setAddPrompt((p) => ({ ...p, open: false, value: '' }));
        return;
      }
      setAddPrompt((p) => ({ ...p, saving: true }));
      const updated = [...(activeSubjectObj.schools || []), clean];
      const updatedData = [...(activeSubjectObj.schoolsData || []), { name: clean, classes: [] }];
      try {
        await persistCourseSettings(activeSubjectId, {
          schools: updated,
          schoolsData: updatedData,
          isMultiSchool: updated.length > 1,
          defaultSchool: clean,
        });
        setSelectedSchool(clean);
        setAddPrompt({ open: false, kind: 'school', value: '', saving: false });
        toast.success(`Added ${clean}`);
      } catch (error) {
        console.error('Error adding school:', error);
        toast.error('Could not add school');
        setAddPrompt((p) => ({ ...p, saving: false }));
      }
    } else {
      const currentClasses = classesForSelectedSchool.map((c: string) => c.toLowerCase());
      if (currentClasses.includes(clean.toLowerCase())) {
        setSelectedClass(clean);
        setAddPrompt((p) => ({ ...p, open: false, value: '' }));
        return;
      }
      setAddPrompt((p) => ({ ...p, saving: true }));
      const updatedClasses = [...classesForSelectedSchool, clean];
      let updatedData = (activeSubjectObj.schoolsData || []).map((s: any) =>
        s.name === selectedSchool ? { ...s, classes: updatedClasses } : s
      );
      if (!updatedData.some((s: any) => s.name === selectedSchool)) {
        updatedData = [...updatedData, { name: selectedSchool, classes: updatedClasses }];
      }
      try {
        await persistCourseSettings(activeSubjectId, {
          schoolsData: updatedData,
          classCourses: updatedClasses,
          defaultClass: clean,
        });
        setSelectedClass(clean);
        setAddPrompt({ open: false, kind: 'class', value: '', saving: false });
        toast.success(`Added ${clean}`);
      } catch (error) {
        console.error('Error adding class:', error);
        toast.error('Could not add class');
        setAddPrompt((p) => ({ ...p, saving: false }));
      }
    }
  };

  // Derive the class list for the currently selected school
  const activeSchoolData = (activeSubjectObj?.schoolsData || []).find(
    (s: { name: string; classes: string[] }) => s.name === selectedSchool
  );
  const classesForSelectedSchool: string[] = (
    activeSchoolData?.classes?.filter((c: string) => c.trim() !== '')?.length
      ? activeSchoolData.classes.filter((c: string) => c.trim() !== '')
      : (activeSubjectObj?.classCourses || []).filter((c: string) => c.trim() !== '')
  );
  const resumeProgressScopeId = buildProgressScopeId(selectedSchool, selectedClass);

  useEffect(() => {
    if (!auth.currentUser || !activeSubjectObj) {
      setResumeProgress({
        progressPercent: 0,
        currentPage: 0,
        totalPages: 0,
        isLoaded: false,
        isCompleted: false,
        completedAt: null,
      });
      return;
    }

    const uid = auth.currentUser.uid;
    const progressRef = doc(
      db,
      `teaching_workspaces/${uid}/courses/${activeSubjectId}/reading_progress`,
      resumeProgressScopeId
    );

    const unsubscribe = onSnapshot(progressRef, (progressSnap) => {
      if (!progressSnap.exists()) {
        setResumeProgress({
          progressPercent: 0,
          currentPage: 0,
          totalPages: 0,
          isLoaded: true,
          isCompleted: false,
          completedAt: null,
        });
        return;
      }

      const data = progressSnap.data();
      setResumeProgress({
        progressPercent: Number(data.progressPercent || 0),
        currentPage: Number(data.currentPage || 0),
        totalPages: Number(data.totalPages || 0),
        isLoaded: true,
        isCompleted: !!data.isCompleted,
        completedAt: data.completedAt?.toDate
          ? data.completedAt.toDate().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
          : null,
      });
    }, (error) => {
      console.error('Error loading resume progress:', error);
      setResumeProgress({
        progressPercent: 0,
        currentPage: 0,
        totalPages: 0,
        isLoaded: true,
        isCompleted: false,
        completedAt: null,
      });
    });

    return () => unsubscribe();
  }, [activeSubjectId, activeSubjectObj?.id, resumeProgressScopeId]);

  if (isLoading) {
    return <WorkspaceLoader minHeight="min-h-[60vh]" />;
  }

  // SUBJECT SELECTION VIEW
  if (!selectedSubject) {
    // Update asset count for dbSubjects
    const dbSubjectsWithAssets = dbSubjects.map(subject => {
      const assets = subjectAssetCounts[subject.id] ?? 0;
      const plans = lessons.filter((l: any) => l.subjectId === subject.id || l.subject === subject.name).length;
      return {
        ...subject,
        stats: {
          ...subject.stats,
          assets,
          plans,
        },
      };
    });
    const allSubjects = dbSubjectsWithAssets;

    return (
      <div className="space-y-8 w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-serif italic lowercase tracking-tight text-4xl font-extrabold"
                style={{ color: 'rgb(249,115,22)' }}>
              My Subjects
            </h1>
            <p className="text-muted-foreground mt-1 text-lg">Select a subject to manage lessons and resources.</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => navigate('/dashboard/lessons/course/add')}
            className="w-fit rounded-full border-slate-200 dark:border-slate-800 bg-white/50 dark:bg-slate-900/50 backdrop-blur-sm hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-bold transition-all"
          >
            <Plus className="mr-2 h-4 w-4" /> Add Course/Subject
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {allSubjects.map((subject) => (
             <motion.div whileHover={{ y: -5 }} key={subject.id}>
                <Card 
                  className="cursor-pointer hover:shadow-xl hover:border-primary/20 transition-all border-gray-100 dark:border-white/5 bg-white dark:bg-white/5 h-full group"
                  onClick={() => handleSelectSubject(subject.name)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="text-xl font-bold">{subject.name}</CardTitle>
                        <CardDescription className="text-xs font-semibold mt-1 uppercase tracking-wider text-muted-foreground">
                          {subject.isMultiSchool ? `${(subject.schools || []).length} school(s)` : (subject.schools?.[0] || 'No school set')}
                        </CardDescription>
                      </div>
                      <div className={`p-2.5 rounded-xl ${subject.theme}`}>
                        <subject.icon className="w-5 h-5" />
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Progress Bar — real Firebase data when available */}
                    {(() => {
                      const realProg = subjectProgressMap[subject.id];
                      const display = realProg !== undefined ? realProg.progressPercent : subject.stats.progress;
                      const completed = realProg?.isCompleted || false;
                      return (
                        <div className="space-y-2">
                          <div className="flex justify-between text-xs font-medium items-center">
                            <span className="text-muted-foreground">Reading Progress</span>
                            {completed ? (
                              <span className="flex items-center gap-1 text-[10px] font-black text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2 py-0.5 rounded-full border border-emerald-200 dark:border-emerald-700/30">
                                <svg className="h-2.5 w-2.5 fill-current" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
                                Complete
                              </span>
                            ) : (
                              <span className="text-gray-900 dark:text-white">{Math.round(display)}%</span>
                            )}
                          </div>
                          <div className="h-2 w-full bg-gray-100 dark:bg-white/10 rounded-full overflow-hidden">
                            <motion.div
                              initial={{ width: 0 }}
                              animate={{ width: `${display}%` }}
                              transition={{ duration: 1, delay: 0.2 }}
                              className={`h-full rounded-full ${completed ? 'bg-emerald-500' : subject.barColor}`}
                            />
                          </div>
                        </div>
                      );
                    })()}

                    {/* Key Metrics */}
                    <div className="grid grid-cols-3 gap-2 py-4 border-y border-gray-100 dark:border-white/5 border-dashed">
                       <div className="text-center">
                          <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{subject.stats.plans}</p>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Plans</p>
                       </div>
                       <div className="text-center border-x border-gray-100 dark:border-white/5 border-dashed px-2">
                          <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{subject.stats.assets}</p>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Assets</p>
                       </div>
                       <div className="text-center">
                          <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{subject.stats.hours}</p>
                          <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider">Hours</p>
                       </div>
                    </div>

                    {/* Footer / Next Up */}
                     <div className="flex items-center justify-between text-sm group-hover:text-primary transition-colors">
                        {(subjectProgressMap[subject.id]?.isCompleted) ? (
                          <div className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400">
                            <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                            <span className="text-xs font-bold">Curriculum Completed</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2 overflow-hidden">
                             <div className="w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0 animate-pulse" />
                             <span className="truncate font-medium text-muted-foreground group-hover:text-primary transition-colors">
                                <span className="text-xs opacity-70 mr-1">NEXT:</span>
                                {nextLessonMap[subject.id] || 'Loading...'}
                             </span>
                          </div>
                        )}
                        <ArrowLeft className="w-4 h-4 rotate-180 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                     </div>
                  </CardContent>
                </Card>
             </motion.div>
          ))}
          
           {/* Add New Subject Card */}
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <button
                type="button"
                onClick={() => navigate('/dashboard/lessons/course/add')}
                className="w-full h-full min-h-[250px] rounded-xl border-2 border-dashed border-gray-200 dark:border-white/10 hover:border-primary/50 hover:bg-primary/5 transition-all flex flex-col items-center justify-center gap-4 group text-muted-foreground hover:text-primary"
              >
                <div className="w-16 h-16 rounded-full bg-gray-50 dark:bg-white/5 group-hover:bg-primary/10 flex items-center justify-center transition-colors shadow-inner">
                  <Plus className="w-8 h-8" />
                </div>
                <div className="text-center space-y-1">
                  <span className="font-bold text-lg block">Add Subject</span>
                  <span className="text-xs text-muted-foreground">Setup a new class curriculum</span>
                </div>
              </button>
            </motion.div>
        </div>
      </div>
    );
  }

  // If we're viewing a PDF, render the full reader
  if (viewResource) {
    return (
      <>
        <PDFReader
          pdfUrl={viewResource.pdfUrl || viewResource.url || ''}
          title={viewResource.title || viewResource.name || 'Resource'}
          onBack={() => { setViewResource(null); setIsFullscreen(false); }}
          setIsFullscreen={setIsFullscreen}
        />
        <Toaster richColors position="bottom-right" />
      </>
    );
  }

  return (
    <>
    <div className="space-y-2 sm:space-y-8 w-full px-2 sm:px-3 md:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-0">
        <div className="flex flex-row sm:flex-row sm:items-center gap-2 sm:gap-4 min-w-0 flex-1">
          <Button variant="ghost" size="icon" onClick={() => handleSelectSubject(null)} className="rounded-full shrink-0 h-9 w-9 sm:h-10 sm:w-10 self-start sm:self-auto bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-200 dark:border-white/10">
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5" />
          </Button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight truncate">{selectedSubject}</h1>
            <p className="text-xs sm:text-sm text-muted-foreground font-medium">Curriculum Dashboard</p>
          </div>
        </div>

        {/* School + Class selectors — on their own row on mobile */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-3 sm:ml-4">
          {/* Multi-school selectors */}
          {(activeSubjectObj?.schools || []).length > 1 && (
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 animate-in fade-in slide-in-from-left-2 duration-300">
              <Select value={selectedSchool} onValueChange={(v) => { if (v === '__add_new_school__') openAddSchoolPrompt(); else handleSelectSchool(v); }}>
                <SelectTrigger className="w-[200px] h-9 rounded-xl bg-white/50 dark:bg-slate-900/50 backdrop-blur-md border-slate-200/60 dark:border-white/10 text-[10px] font-black uppercase tracking-[0.05em] shadow-sm hover:shadow-md hover:border-indigo-500/30 transition-all group ring-offset-white dark:ring-offset-slate-950 focus:ring-indigo-500/20">
                  <div className="flex items-center gap-2 truncate pr-2">
                    <div className="h-5 w-5 rounded-lg bg-indigo-50 dark:bg-indigo-900/20 flex items-center justify-center group-hover:bg-indigo-600 group-hover:text-white transition-colors">
                      <Building2 className="h-3 w-3 text-indigo-500 group-hover:text-white shrink-0" />
                    </div>
                    <span className="truncate text-slate-600 dark:text-slate-300">
                      <SelectValue placeholder="SELECT SCHOOL" />
                    </span>
                  </div>
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-slate-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl p-1 animate-in zoom-in-95 duration-200">
                  {(activeSubjectObj.schools || []).map((school: string) => (
                    <SelectItem 
                      key={school} 
                      value={school} 
                      className="text-xs font-bold font-sans rounded-xl mb-1 last:mb-0 focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 dark:focus:text-indigo-400 cursor-pointer transition-colors py-2.5"
                    >
                      <div className="flex items-center gap-2">
                        <span>{school}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                  <SelectItem
                    value="__add_new_school__"
                    className="text-xs font-bold font-sans rounded-xl focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 dark:focus:text-indigo-400 cursor-pointer py-2.5 text-indigo-600 dark:text-indigo-400"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add another school…</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Class selector — only for multi-school subjects; single-school subjects get their
              class dropdown inside the single-school badge block below to avoid duplicates */}
          {(activeSubjectObj?.schools || []).length > 1 && selectedSchool && (
            <div className="sm:pl-3 flex flex-wrap items-center gap-2 animate-in fade-in slide-in-from-left-2 duration-300">
              <Layers2 className="h-3.5 w-3.5 text-indigo-400 shrink-0" />
              <Select value={selectedClass} onValueChange={(v) => { if (v === '__add_new_class__') openAddClassPrompt(); else handleSelectClass(v); }}>
                <SelectTrigger className="w-[180px] h-9 rounded-xl bg-white/50 dark:bg-slate-900/50 backdrop-blur-md border-slate-200/60 dark:border-white/10 text-[10px] font-black uppercase tracking-[0.05em] shadow-sm hover:shadow-md hover:border-indigo-500/30 transition-all ring-offset-white dark:ring-offset-slate-950 focus:ring-indigo-500/20">
                  <span className="truncate text-slate-600 dark:text-slate-300">
                    <SelectValue placeholder="SELECT CLASS" />
                  </span>
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-slate-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl p-1 animate-in zoom-in-95 duration-200">
                  {classesForSelectedSchool.map((cls: string) => (
                    <SelectItem
                      key={cls}
                      value={cls}
                      className="text-xs font-bold font-sans rounded-xl mb-1 last:mb-0 focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 dark:focus:text-indigo-400 cursor-pointer py-2.5"
                    >
                      <div className="flex items-center gap-2">
                        <span>{cls}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                  <SelectItem
                    value="__add_new_class__"
                    className="text-xs font-bold font-sans rounded-xl focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 dark:focus:text-indigo-400 cursor-pointer py-2.5 text-indigo-600 dark:text-indigo-400"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add another class…</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Single-school badge (no dropdown needed) */}
          {(activeSubjectObj?.schools || []).length === 1 && (
            <div className="sm:ml-4 sm:pl-4 sm:border-l border-gray-100 dark:border-white/5 flex flex-wrap items-center gap-2 h-auto sm:h-10 animate-in fade-in duration-300">
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-100 dark:border-indigo-800/40">
                <Building2 className="h-3 w-3 text-indigo-500" />
                <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400">{activeSubjectObj.schools[0]}</span>
              </div>
              <Select value={selectedClass} onValueChange={(v) => { if (v === '__add_new_class__') openAddClassPrompt(); else handleSelectClass(v); }}>
                <SelectTrigger className="w-[160px] h-8 rounded-xl bg-white/50 dark:bg-slate-900/50 backdrop-blur-md border-slate-200/60 dark:border-white/10 text-[10px] font-black uppercase tracking-[0.05em] shadow-sm hover:shadow-md hover:border-indigo-500/30 transition-all">
                  <span className="truncate text-slate-600 dark:text-slate-300">
                    <SelectValue placeholder="SELECT CLASS" />
                  </span>
                </SelectTrigger>
                <SelectContent className="rounded-2xl border-slate-200 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-xl shadow-2xl p-1 animate-in zoom-in-95 duration-200">
                  {classesForSelectedSchool.map((cls: string) => (
                    <SelectItem key={cls} value={cls} className="text-xs font-bold font-sans rounded-xl mb-1 last:mb-0 focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 cursor-pointer py-2.5">
                      <div className="flex items-center gap-2">
                        <span>{cls}</span>
                      </div>
                    </SelectItem>
                  ))}
                  <div className="h-px bg-slate-200 dark:bg-white/10 my-1" />
                  <SelectItem
                    value="__add_new_class__"
                    className="text-xs font-bold font-sans rounded-xl focus:bg-indigo-50 dark:focus:bg-indigo-900/40 focus:text-indigo-600 dark:focus:text-indigo-400 cursor-pointer py-2.5 text-indigo-600 dark:text-indigo-400"
                  >
                    <div className="flex items-center gap-2">
                      <Plus className="h-3 w-3" />
                      <span>Add another class…</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

        </div>
      </div>

      {/* Curriculum Management entry point */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4">
        <Card className="border border-indigo-100 dark:border-indigo-900/40 bg-indigo-50/30 dark:bg-indigo-950/20 p-3 sm:p-4 rounded-xl flex flex-col gap-3 sm:gap-4 hover:shadow-lg transition-all">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 sm:gap-0">
            <div className="flex items-center gap-4">
              <div className="h-10 w-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center shadow-xl shadow-indigo-200 dark:shadow-none transition-transform shrink-0">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-white uppercase tracking-tight">Curriculum Dashboard</h3>
                <p className="text-xs text-muted-foreground font-medium">Manage course outline, pedagogical goals, and syllabus structure in one place.</p>
              </div>
            </div>
            <Button
              variant="outline"
              onClick={openStructurePage}
              disabled={isOpeningStructure}
              className="rounded-lg h-9 px-4 text-xs font-semibold bg-white dark:bg-slate-900 border-indigo-100 dark:border-indigo-800 text-indigo-600 hover:bg-indigo-50 shadow-sm self-start sm:self-auto"
            >
              {isOpeningStructure
                ? <><Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Opening…</>
                : <>Manage Resource <ArrowLeft className="ml-2 h-3 w-3 rotate-180" /></>}
            </Button>
          </div>
        </Card>
      </div>

      <AlertDialog open={!!lessonToDelete} onOpenChange={(open) => !open && setLessonToDelete(null)}>
        <AlertDialogContent className="bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900 dark:text-white">Delete Lesson Plan?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500 dark:text-slate-400">
              Are you sure you want to delete this lesson plan? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteLesson}
              className="bg-red-600 hover:bg-red-700 text-white"
              disabled={isDeletingLesson}
            >
              {isDeletingLesson ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-gray-200 dark:border-white/10 pb-1 overflow-x-auto no-scrollbar">
        <Button 
           variant="ghost" 
           onClick={() => setActiveTab('resources')} 
           className={`rounded-none border-b-2 px-4 sm:px-6 whitespace-nowrap shrink-0 ${activeTab === 'resources' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground'}`}
        >
           <Folder className="mr-2 h-4 w-4" /> Outline & Resources
        </Button>
        <Button 
           variant="ghost" 
           onClick={() => setActiveTab('plans')} 
           className={`rounded-none border-b-2 px-4 sm:px-6 whitespace-nowrap shrink-0 ${activeTab === 'plans' ? 'border-primary text-primary bg-primary/5' : 'border-transparent text-muted-foreground'}`}
        >
           <Layout className="mr-2 h-4 w-4" /> Lesson Plans
        </Button>
      </div>

      {/* LESSON PLANS TAB */}
      {activeTab === 'plans' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 sm:gap-0">
            <div className="relative w-full sm:max-w-md group">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input 
                 placeholder={`Search ${selectedSubject} lessons...`}
                 className="pl-10 h-10 bg-white dark:bg-white/5 border-gray-200 dark:border-white/10 rounded-full"
                 value={searchTerm}
                 onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
              />
            </div>
             <Button 
               onClick={() => navigate('/dashboard/lessons/new', { state: { subjectId: activeSubjectId, subjectName: selectedSubject, syllabus: syllabusData, from: `/dashboard/lessons` } })}
                className="h-9 px-4 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-sm shadow-indigo-500/10 transition-all flex items-center justify-center gap-2 active:scale-[0.98] border-none w-full sm:w-auto"
              >
                <Plus className="h-4 w-4" />
                <span>New Lesson Plan</span>
              </Button>
          </div>

          <div className="grid gap-6 md:grid-cols-2">
            <AnimatePresence mode="popLayout">
              {filteredLessons.map((lesson) => (
                <motion.div
                  key={lesson.id}
                  initial={{ scale: 0.95, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card className="group relative overflow-hidden border-none bg-card hover:shadow-2xl transition-all duration-300">
                    <div className={`absolute left-0 top-0 bottom-0 w-1.5 transition-colors ${lesson.status === 'published' ? 'bg-green-500' : 'bg-amber-500'}`} />
                    <CardHeader className="flex flex-row items-start justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="bg-primary/10 text-primary hover:bg-primary/20 transition-colors">
                            {lesson.subject}
                          </Badge>
                          <Badge variant="outline" className={lesson.status === 'published' ? 'text-green-600 border-green-200' : 'text-amber-600 border-amber-200'}>
                            {lesson.status}
                          </Badge>
                        </div>
                        <CardTitle className="text-2xl font-bold line-clamp-1">{lesson.title}</CardTitle>
                        <CardDescription className="flex items-center gap-2">
                          <Calendar className="h-4 w-4" /> Scheduled: {lesson.date}
                        </CardDescription>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
                            <MoreVertical className="h-5 w-5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setLessonToDelete(lesson.id)} className="text-rose-600 cursor-pointer">
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 sm:gap-0 mt-4">
                        <div className="flex gap-4 text-sm text-muted-foreground">
                           <span className="flex items-center gap-1">
                              <Clock className="h-4 w-4" /> 55 min
                           </span>
                        </div>
                        <div className="flex gap-2 w-full sm:w-auto">
                          <Button variant="outline" size="sm" className="rounded-lg flex-1 sm:flex-initial" onClick={() => navigate(`/dashboard/lessons/edit/${lesson.id}`)}>
                            <Edit2 className="h-4 w-4 mr-2" /> Edit
                          </Button>
                          <Button 
                            size="sm" 
                            className="rounded-lg group/btn flex-1 sm:flex-initial"
                            onClick={() => navigate(`/dashboard/lessons/view/${lesson.id}`)}
                          >
                            View <ChevronRight className="h-4 w-4 ml-1 group-hover/btn:translate-x-1 transition-transform" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
          
          {filteredLessons.length === 0 && (
             <div className="text-center py-12 border-2 border-dashed border-gray-100 dark:border-white/5 rounded-3xl">
               <p className="text-muted-foreground">No plans found for {selectedSubject}. Create your first one!</p>
             </div>
          )}
        </motion.div>
      )}

      {/* RESOURCES TAB */}
      {activeTab === 'resources' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-8 pb-20">
            {/* Syllabus Preview Section */}
            <div className="space-y-4">
              {/* Progress Indicator */}
              <div className="bg-slate-50/50 dark:bg-slate-900/30 border border-slate-100 dark:border-white/5 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 group/progress transition-all hover:bg-slate-50 dark:hover:bg-slate-900/50">
                <div className="flex-1 space-y-2.5">
                  <div className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.2em] text-slate-400 dark:text-slate-500">
                    <span>Curriculum Completion</span>
                    {(resumeProgress.isCompleted || resumeProgress.progressPercent >= 100) ? (
                      <span className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/20 px-2.5 py-1 rounded-full border border-emerald-200 dark:border-emerald-700/30">
                        <svg className="h-3 w-3 fill-current" viewBox="0 0 20 20"><path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/></svg>
                        COMPLETED
                      </span>
                    ) : (
                      <span className="text-indigo-600 dark:text-indigo-400 font-bold">{Math.round(resumeProgress.progressPercent)}%</span>
                    )}
                  </div>
                  <div className="h-1.5 w-full bg-slate-200/50 dark:bg-slate-800/50 rounded-full overflow-hidden">
                    <motion.div 
                      initial={{ width: 0 }}
                      animate={{ width: `${(resumeProgress.isCompleted || resumeProgress.progressPercent >= 100) ? 100 : resumeProgress.progressPercent}%` }}
                      transition={{ duration: 1, ease: "easeOut" }}
                      className={`h-full rounded-full shadow-[0_0_12px_rgba(99,102,241,0.3)] ${
                        (resumeProgress.isCompleted || resumeProgress.progressPercent >= 100)
                          ? 'bg-emerald-500 dark:bg-emerald-400 shadow-[0_0_12px_rgba(16,185,129,0.3)]'
                          : 'bg-indigo-500 dark:bg-indigo-400'
                      }`}
                    />
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 dark:text-slate-400">
                    <span>
                      {selectedSchool || 'All schools'}
                      {selectedClass ? ` • ${selectedClass}` : ''}
                    </span>
                    <span className="font-semibold text-slate-600 dark:text-slate-300">
                      {(resumeProgress.isCompleted || resumeProgress.progressPercent >= 100)
                        ? (resumeProgress.completedAt ? `Completed ${resumeProgress.completedAt}` : 'Completed')
                        : resumeProgress.totalPages > 0
                          ? `${Math.max(0, resumeProgress.currentPage)} / ${resumeProgress.totalPages} pages`
                          : (resumeProgress.isLoaded ? 'Not started' : 'Loading...')}
                    </span>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Start Again — always available when any progress exists */}
                  {resumeProgress.progressPercent > 0 && !resumeProgress.isCompleted && resumeProgress.progressPercent < 100 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      className="h-9 px-3 rounded-xl border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:text-rose-500 hover:border-rose-300 dark:hover:border-rose-500/30 hover:bg-rose-50 dark:hover:bg-rose-900/10 transition-all active:scale-95 flex items-center gap-2 font-bold text-xs"
                    onClick={async () => {
                        if (!auth.currentUser || !activeSubjectId) return;
                        try {
                          const uid = auth.currentUser.uid;
                          const progressRef = doc(db, `teaching_workspaces/${uid}/courses/${activeSubjectId}/reading_progress`, resumeProgressScopeId);
                          // Use setDoc (full overwrite) instead of updateDoc so the reset is atomic
                          await setDoc(progressRef, { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0, isCompleted: false, completedAt: null, schoolName: selectedSchool, className: selectedClass, updatedAt: serverTimestamp() });
                          setResumeProgress(prev => ({ ...prev, progressPercent: 0, currentPage: 0, isCompleted: false, completedAt: null }));
                          navigate('/dashboard/lessons/course/content', {
                            state: {
                              isFullSubjectView: true, subjectId: activeSubjectId, subjectName: selectedSubject,
                              syllabusData, subContentMap, fromPath: '/dashboard/lessons',
                              fromState: { subjectId: activeSubjectId, subjectName: selectedSubject, isFirebaseSubject: !!activeSubjectObj, selectedSchool, selectedClass },
                              isFirebaseSubject: !!activeSubjectObj, contentTitle: selectedSubject,
                              selectedSchool, selectedClass, progressScopeId: resumeProgressScopeId,
                              forceProgressReload: true,
                            }
                          });
                        } catch (err) {
                          console.error('Failed to reset progress:', err);
                          toast.error('Failed to reset progress');
                        }
                      }}
                    >
                      ↺ Start Again
                    </Button>
                  )}
                  {/* Primary action: Resume / Start / Start Again (at 100%) */}
                  <Button
                    size="sm"
                    className="h-9 px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white shadow-md shadow-indigo-500/10 transition-all active:scale-95 flex items-center gap-2 font-bold text-xs"
                    onClick={async () => {
                      let forceReload = false;
                      // Reset if completed OR at 100%
                      if ((resumeProgress.isCompleted || resumeProgress.progressPercent >= 100) && auth.currentUser && activeSubjectId) {
                        try {
                          const uid = auth.currentUser.uid;
                          const progressRef = doc(db, `teaching_workspaces/${uid}/courses/${activeSubjectId}/reading_progress`, resumeProgressScopeId);
                          // Use setDoc (full overwrite) so the reset is atomic and cache-safe
                          await setDoc(progressRef, { progressPercent: 0, currentPage: 0, totalPages: 0, scrollTop: 0, isCompleted: false, completedAt: null, schoolName: selectedSchool, className: selectedClass, updatedAt: serverTimestamp() });
                          setResumeProgress(prev => ({ ...prev, progressPercent: 0, currentPage: 0, isCompleted: false, completedAt: null }));
                          forceReload = true;
                        } catch (err) {
                          console.error('Failed to reset progress:', err);
                          toast.error('Failed to reset progress');
                          return;
                        }
                      }
                      navigate('/dashboard/lessons/course/content', {
                        state: {
                          isFullSubjectView: true, subjectId: activeSubjectId, subjectName: selectedSubject,
                          syllabusData, subContentMap, fromPath: '/dashboard/lessons',
                          fromState: { subjectId: activeSubjectId, subjectName: selectedSubject, isFirebaseSubject: !!activeSubjectObj, selectedSchool, selectedClass },
                          isFirebaseSubject: !!activeSubjectObj, contentTitle: selectedSubject,
                          selectedSchool, selectedClass, progressScopeId: resumeProgressScopeId,
                          forceProgressReload: forceReload,
                        }
                      });
                    }}
                  >
                    {(resumeProgress.isCompleted || resumeProgress.progressPercent >= 100)
                      ? <>Start again <ArrowRight className="h-3.5 w-3.5" /></>
                      : resumeProgress.progressPercent > 0
                        ? <>Resume <ArrowRight className="h-3.5 w-3.5" /></>
                        : <>Start <ArrowRight className="h-3.5 w-3.5" /></>}
                  </Button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 sm:gap-0">
                <div>
                   <h3 className="text-xl font-bold flex items-center gap-2">
                     <LayoutList className="h-5 w-5 text-indigo-500" /> Syllabus & Curriculum
                   </h3>
                   <p className="text-sm text-muted-foreground">Manage your course outline and learning topics.</p>
                </div>
                <Button 
                  variant="outline" 
                  onClick={() => navigate('/dashboard/lessons/course/structure', { state: { subjectId: activeSubjectId, subjectName: selectedSubject, isFirebaseSubject: !!activeSubjectObj } })}
                  className="rounded-xl border-indigo-200 dark:border-indigo-900/50 text-indigo-600 dark:text-indigo-400 bg-indigo-50/50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 transition-all flex items-center gap-2 self-start sm:self-auto"
                >
                  <PenLine className="h-4 w-4" /> Manage Syllabus
                </Button>
             </div>

             <div className="flex flex-col gap-6">
                <Card 
                   className="border-none bg-slate-50 dark:bg-slate-900/50 p-6 rounded-2xl cursor-pointer hover:bg-slate-100 dark:hover:bg-slate-800/80 transition-all group"
                   onClick={() => {
                     if (syllabusData?.hasAnyContent) {
                        navigate('/dashboard/lessons/course/content', { 
                           state: { 
                             directPdfUrl: syllabusData?.referencePdfUrl || null,
                             htmlContent: syllabusData?.outlineContent || null,
                             fromPath: '/dashboard/lessons',
                             fromState: {
                               subjectId: activeSubjectId,
                               subjectName: selectedSubject,
                               isFirebaseSubject: !!activeSubjectObj,
                               selectedSchool,
                               selectedClass,
                             },
                             subjectId: activeSubjectId,
                             subjectName: selectedSubject,
                             isFirebaseSubject: !!activeSubjectObj,
                             selectedSchool,
                             selectedClass,
                             contentTitle: selectedSubject,
                             isFullSubjectView: false,
                             syllabusData: null,
                             subContentMap: null
                           } 
                         });
                      } else {
                        toast.info("No general outline content available for this subject.");
                      }
                    }}
                >
                   <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-2">
                         <Target className="h-4 w-4 text-indigo-500" />
                         <span className="text-xs font-bold uppercase tracking-widest text-slate-500">General Outline</span>
                      </div>
                      <ArrowRight className="h-4 w-4 text-slate-300 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                   </div>
                   {activeSubjectObj ? (
                      syllabusData?.hasOutline ? (
                        <div 
                          className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3 prose prose-sm prose-slate dark:prose-invert"
                          dangerouslySetInnerHTML={{ __html: syllabusData.outlineContent }} 
                        />
                      ) : syllabusData?.hasPdf ? (
                        <p className="text-sm text-slate-500 dark:text-slate-400">PDF outline uploaded for this subject.</p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">No outline added yet.</p>
                      )
                   ) : (
                      <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed line-clamp-3">
                        This course provides a comprehensive introduction to {selectedSubject}. Students will explore core concepts, practical applications, and advanced theoretical frameworks through active learning and project-based assessments.
                      </p>
                   )}
                </Card>

                <Card className="border-none bg-slate-50 dark:bg-slate-900/50 p-6 rounded-2xl">
                   <div className="flex items-center gap-2 mb-4">
                      <LayoutList className="h-4 w-4 text-emerald-500" />
                      <span className="text-xs font-bold uppercase tracking-widest text-slate-500">Curriculum Breakdown</span>
                   </div>
                   <div className="space-y-2">
                     {activeSubjectObj ? (
                       syllabusData?.topics && syllabusData.topics.length > 0 ? (
                         syllabusData.topics.slice(0, 3).map((topic: any, i: number) => (
                           <div key={topic.id || i} className="rounded-xl overflow-hidden border border-slate-200 dark:border-slate-800">
                             {/* ── TOPIC HEADER ROW ── */}
                             <div 
                               onClick={(e) => {
                                 e.stopPropagation();
                                 if (topic.subTopics && topic.subTopics.length > 0) {
                                   setExpandedTopicId(expandedTopicId === topic.id ? null : topic.id);
                                 } else {
                                   const contentKey = `${topic.id}_main`;
                                   const topicContent = subContentMap[contentKey];
                                   if (topicContent?.hasAnyContent) {
                                     navigate('/dashboard/lessons/course/content', { 
                                       state: { 
                                         directPdfUrl: topicContent?.referencePdfUrl || null,
                                         htmlContent: topicContent?.outlineContent || null,
                                         fromPath: '/dashboard/lessons',
                                         fromState: {
                                           subjectId: activeSubjectId,
                                           subjectName: selectedSubject,
                                           isFirebaseSubject: !!activeSubjectObj,
                                           selectedSchool,
                                           selectedClass,
                                         },
                                         subjectId: activeSubjectId,
                                         subjectName: selectedSubject,
                                         isFirebaseSubject: !!activeSubjectObj,
                                         selectedSchool,
                                         selectedClass,
                                         contentTitle: topic.title,
                                         isFullSubjectView: false,
                                         syllabusData: null,
                                         subContentMap: null
                                       } 
                                     });
                                   } else {
                                      toast.custom((t) => (
                                        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-r border-b border-slate-200 dark:border-white/10 border-l-4 border-l-indigo-500 rounded-2xl shadow-xl p-4 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-5 duration-300">
                                          <div className="h-10 w-10 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center shrink-0 border border-indigo-100 dark:border-indigo-500/20">
                                            <AlertCircle className="h-5 w-5 text-indigo-500" />
                                          </div>
                                          <div className="flex flex-col gap-0.5">
                                            <span className="text-xs font-black uppercase tracking-widest text-indigo-600 dark:text-indigo-400">Empty Section</span>
                                            <span className="text-[11px] font-bold text-slate-900 dark:text-slate-100">the topic or subtopic is empty</span>
                                          </div>
                                        </div>
                                      ));
                                   }
                                 }
                               }}
                               className="flex items-center justify-between px-3 py-2.5 bg-slate-50 dark:bg-slate-900 cursor-pointer group/topic hover:bg-indigo-50 dark:hover:bg-indigo-950/40 transition-colors"
                             >
                                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                                  <span className="text-[10px] font-black text-white bg-indigo-500 px-1.5 py-0.5 rounded shrink-0 tracking-wider">T{i + 1}</span>
                                  <span className="text-sm font-bold text-slate-800 dark:text-slate-100 line-clamp-1 group-hover/topic:text-indigo-600 dark:group-hover/topic:text-indigo-400 transition-colors">{topic.title}</span>
                                  {/* warn if topic has no subtopics and no content */}
                                  {subContentLoaded && (!topic.subTopics?.length) && !subContentMap[`${topic.id}_main`]?.hasAnyContent && (
                                    <span title="No content uploaded" className="shrink-0 h-3.5 w-3.5 rounded-full bg-amber-400 dark:bg-amber-500 flex items-center justify-center text-white text-[8px] font-black leading-none cursor-help">!</span>
                                  )}
                                </div>
                                <div className="flex items-center gap-2 shrink-0 ml-2">
                                  {topic.subTopics?.length > 0 && (
                                    <span className="text-[9px] font-bold text-slate-400 uppercase tracking-wider">{topic.subTopics.length} modules</span>
                                  )}
                                  {expandedTopicId === topic.id && topic.subTopics?.length > 0
                                    ? <ChevronDown className="h-3.5 w-3.5 text-indigo-500" />
                                    : <ChevronRight className="h-3.5 w-3.5 text-slate-400 group-hover/topic:text-indigo-500 transition-colors" />}
                                </div>
                             </div>

                             {/* ── SUBTOPICS ── */}
                             <AnimatePresence>
                               {expandedTopicId === topic.id && topic.subTopics && (
                                 <motion.div 
                                   initial={{ height: 0, opacity: 0 }}
                                   animate={{ height: 'auto', opacity: 1 }}
                                   exit={{ height: 0, opacity: 0 }}
                                   className="overflow-hidden"
                                 >
                                   <div className="divide-y divide-slate-100 dark:divide-slate-800/60">
                                     {topic.subTopics.map((st: any, sIdx: number) => (
                                       (() => {
                                         const contentKey = `${topic.id}_${st.id}`;
                                         const stContent = subContentMap[contentKey] || (sIdx === 0 ? subContentMap[`${topic.id}_main`] : null);
                                         return (
                                       <div 
                                         key={st.id || sIdx}
                                         onClick={(e) => {
                                           e.stopPropagation();
                                           if (stContent?.hasAnyContent) {
                                             navigate('/dashboard/lessons/course/content', { 
                                               state: { 
                                                 directPdfUrl: stContent?.referencePdfUrl || null,
                                                 htmlContent: stContent?.outlineContent || null,
                                                 fromPath: '/dashboard/lessons',
                                                 fromState: {
                                                   subjectId: activeSubjectId,
                                                   subjectName: selectedSubject,
                                                   isFirebaseSubject: !!activeSubjectObj,
                                                   selectedSchool,
                                                   selectedClass,
                                                 },
                                                 subjectId: activeSubjectId,
                                                 subjectName: selectedSubject,
                                                 isFirebaseSubject: !!activeSubjectObj,
                                                 selectedSchool,
                                                 selectedClass,
                                                 contentTitle: st.title
                                               } 
                                             });
                                           } else {
                                              toast.custom((t) => (
                                                <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-r border-b border-slate-200 dark:border-white/10 border-l-4 border-l-orange-500 rounded-2xl shadow-xl p-4 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-5 duration-300">
                                                  <div className="h-10 w-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 flex items-center justify-center shrink-0 border border-orange-100 dark:border-orange-500/20">
                                                    <AlertCircle className="h-5 w-5 text-orange-500" />
                                                  </div>
                                                  <div className="flex flex-col gap-0.5">
                                                    <span className="text-xs font-black uppercase tracking-widest text-orange-600 dark:text-orange-400">Missing Resource</span>
                                                    <span className="text-[11px] font-bold text-slate-900 dark:text-slate-100">the topic or subtopic is empty</span>
                                                  </div>
                                                </div>
                                              ));
                                           }
                                         }}
                                         className="flex items-center justify-between py-2 pl-8 pr-3 bg-white dark:bg-slate-950 hover:bg-indigo-50/60 dark:hover:bg-indigo-900/10 cursor-pointer group/sub transition-colors"
                                       >
                                         <div className="flex items-center gap-2 min-w-0">
                                           <span className="text-[9px] font-black text-indigo-400 dark:text-indigo-500 shrink-0 tabular-nums">{i + 1}.{sIdx + 1}</span>
                                           <span className="text-xs font-medium text-slate-500 dark:text-slate-400 group-hover/sub:text-indigo-600 dark:group-hover/sub:text-indigo-400 transition-colors line-clamp-1">{st.title}</span>
                                           {subContentLoaded && !stContent?.hasAnyContent && (
                                             <span title="No content uploaded" className="shrink-0 h-3.5 w-3.5 rounded-full bg-amber-400 dark:bg-amber-500 flex items-center justify-center text-white text-[8px] font-black leading-none cursor-help">!</span>
                                           )}
                                         </div>
                                         <ArrowRight className="h-3 w-3 text-slate-300 dark:text-slate-600 opacity-0 -translate-x-1 group-hover/sub:opacity-100 group-hover/sub:translate-x-0 group-hover/sub:text-indigo-400 transition-all shrink-0 ml-2" />
                                       </div>
                                       );
                                      })()
                                     ))}
                                   </div>
                                 </motion.div>
                               )}
                             </AnimatePresence>
                           </div>
                         ))
                       ) : (
                         <p className="text-sm text-slate-400 italic">No topics added yet.</p>
                       )
                     ) : (
                        [1, 2, 3].map(i => (
                          <div 
                            key={i} 
                            onClick={(e) => {
                              e.stopPropagation();
                               toast.custom((t) => (
                                 <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-t border-r border-b border-slate-200 dark:border-white/10 border-l-4 border-l-indigo-500 rounded-2xl shadow-xl p-4 flex items-center gap-4 animate-in fade-in slide-in-from-bottom-5 duration-300">
                                   <div className="h-10 w-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center shrink-0 border border-slate-200 dark:border-white/5">
                                     <AlertCircle className="h-5 w-5 text-slate-400" />
                                   </div>
                                   <div className="flex flex-col gap-0.5">
                                     <span className="text-xs font-black uppercase tracking-widest text-slate-500">Placeholder Data</span>
                                     <span className="text-[11px] font-bold text-slate-900 dark:text-slate-100">the topic or subtopic is empty</span>
                                   </div>
                                 </div>
                               ));
                            }}
                            className="flex items-center justify-between text-sm py-2 px-3 -mx-3 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-all cursor-pointer group/topic"
                          >
                             <span className="text-slate-700 dark:text-slate-300 font-medium group-hover/topic:text-indigo-600 dark:group-hover/topic:text-indigo-400 transition-colors">Topic {i}: Core Fundamentals</span>
                             <ChevronRight className="h-3 w-3 text-slate-300" />
                          </div>
                        ))
                     )}
                   </div>
                </Card>
             </div>
           </div>

           <div className="h-px w-full bg-slate-100 dark:bg-slate-800/50" />
           
             <div className="space-y-4">
               <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-y-3 sm:gap-0">
                 <div>
                   <h3 className="font-bold text-indigo-900 dark:text-indigo-100 text-lg">Course Materials</h3>
                   <p className="text-indigo-600 dark:text-indigo-300 text-sm">Access schemes of work, textbooks, and essential learning resources for your course.</p>
                 </div>
                 <Button
                   type="button"
                   variant="outline"
                   onClick={() => navigate('/dashboard/lessons/course/materials', { state: { subjectId: activeSubjectId, subjectName: selectedSubject, from: '/dashboard/lessons' } })}
                   className="rounded-full border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 font-bold self-start sm:self-auto"
                 >
                   <Library className="mr-2 h-4 w-4" /> View All
                 </Button>
               </div>
             </div>
           
           <div className="space-y-4">
             {subjectResources.length > 0 ? (
               <>
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                   {subjectResources.slice(0, 3).map((file) => (
                     <PersonalResourceCard
                       key={file.id}
                       resource={file}
                        onView={(r) => handleViewResource(r)}
                        onDownload={(r) => handleDownloadResource(r.pdfUrl || (r as any).url || '', r.title || (r as any).name || 'Untitled', r.id)}
                       onDelete={(r) => handleDeleteResource(r.id)}
                       onFavourite={handleFavouriteResource}
                       isFavourite={favourites.has(file.id)}
                       isDownloading={downloadingResourceId === file.id}
                       isDeleting={false}
                     />
                   ))}
                 </div>
                 {subjectResources.length > 3 && (
                   <Button
                     type="button"
                     variant="outline"
                     onClick={() => navigate('/dashboard/lessons/course/materials', { state: { subjectId: activeSubjectId, subjectName: selectedSubject, from: '/dashboard/lessons' } })}
                     className="w-full rounded-full border-indigo-200 dark:border-indigo-500/30 text-indigo-600 dark:text-indigo-300 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 font-bold"
                   >
                     View More ({subjectResources.length - 3} more) <ChevronRight className="ml-2 h-4 w-4" />
                   </Button>
                 )}
               </>
             ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-100 dark:border-white/5 rounded-3xl">
                  <p className="text-muted-foreground flex items-center justify-center gap-2">
                     <Folder className="h-5 w-5 text-gray-400" />
                     No materials uploaded for {selectedSubject} yet.
                  </p>
                </div>
             )}
           </div>
        </motion.div>
      )}
        <Toaster richColors position="bottom-right" />
    </div>

    <Dialog open={addPrompt.open} onOpenChange={(o) => { if (!o) closeAddPrompt(); }}>
      <DialogContent className="max-w-md w-[calc(100%-2rem)] rounded-3xl border-slate-200/60 dark:border-white/10 bg-white/95 dark:bg-[#0f172a]/95 backdrop-blur-2xl shadow-2xl p-0 overflow-hidden">
        <div className="px-6 pt-6 pb-2">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-2xl bg-indigo-50 dark:bg-indigo-900/30 flex items-center justify-center shrink-0">
              {addPrompt.kind === 'school' ? (
                <Building2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              ) : (
                <GraduationCap className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
              )}
            </div>
            <div className="flex-1">
              <DialogTitle className="text-base font-black tracking-tight text-slate-900 dark:text-white">
                {addPrompt.kind === 'school' ? 'Add a new school' : 'Add a new class'}
              </DialogTitle>
              <DialogDescription className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {addPrompt.kind === 'school'
                  ? 'Add another institution you teach this subject at.'
                  : `Add another class or course for ${selectedSchool || 'this school'}.`}
              </DialogDescription>
            </div>
          </div>
        </div>

        <div className="px-6 pb-4">
          <Input
            autoFocus
            disabled={addPrompt.saving}
            value={addPrompt.value}
            onChange={(e) => setAddPrompt((p) => ({ ...p, value: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleConfirmPrompt();
              }
            }}
            placeholder={addPrompt.kind === 'school' ? 'e.g. Greenfield High School' : 'e.g. Form 3, Form 4 — Term 1'}
            className="h-11 rounded-xl border-slate-200/60 dark:border-white/10 bg-white/70 dark:bg-slate-900/60 text-sm font-medium focus-visible:ring-2 focus-visible:ring-indigo-500/30 focus-visible:border-indigo-500/50"
          />
        </div>

        <div className="px-6 pb-6 pt-2 border-t border-slate-100 dark:border-white/5 bg-slate-50/50 dark:bg-slate-900/30 flex items-center justify-end gap-2">
          <Button
            type="button"
            variant="ghost"
            disabled={addPrompt.saving}
            onClick={closeAddPrompt}
            className="rounded-xl h-9 px-4 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-white/5"
          >
            Cancel
          </Button>
          <Button
            type="button"
            disabled={addPrompt.saving || !addPrompt.value.trim()}
            onClick={handleConfirmPrompt}
            className="rounded-xl h-9 px-5 text-xs font-black bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {addPrompt.saving ? (
              <span className="flex items-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Saving…
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <Plus className="h-3.5 w-3.5" />
                Add
              </span>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
    </>
  );
};

export default LessonPlannerView;
